import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, getTrySessionId } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ArrowLeft, Sparkles, MessageSquare, CheckCircle2, Zap, GitBranch, Share2, Wand2, Target } from 'lucide-react';
import { AgentAvatar } from '@/components/AgentAvatar';
import { AGENT_DETAIL } from '@/constants/testIds';
import { toast } from 'sonner';

export default function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const trySessionId = React.useMemo(() => getTrySessionId(), []);

  const { data: agent, isLoading } = useQuery({
    queryKey: ['agent', id],
    queryFn: async () => (await api.get(`/agents/${id}`)).data,
  });

  const { data: anonUsage } = useQuery({
    queryKey: ['anon-remaining', id, trySessionId, !!user],
    queryFn: async () => {
      const r = await api.get(`/agents/${id}/anon-remaining?try_session_id=${trySessionId}`);
      return r.data;
    },
    enabled: !!id && !user,
  });

  const remaining = anonUsage?.remaining ?? anonUsage?.cap_per_agent ?? 2;

  if (isLoading) {
    return <div className="max-w-5xl mx-auto px-4 pt-12 text-white/60">Loading…</div>;
  }
  if (!agent) {
    return <div className="max-w-5xl mx-auto px-4 pt-12 text-white/60">Agent not found.</div>;
  }

  const onShare = async () => {
    const url = `${window.location.origin}/share/${agent.agent_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch {
      toast.message('Share link', { description: url });
    }
  };

  const onTryStarter = () => {
    if (agent.starter_prompt) {
      const prefill = encodeURIComponent(agent.starter_prompt);
      navigate(`/chat?agent=${agent.agent_id}&prefill=${prefill}`);
    } else {
      navigate(`/chat?agent=${agent.agent_id}`);
    }
  };

  const tryLabel = user ? 'Try Now' : `Try Now — ${remaining} free ${remaining === 1 ? 'try' : 'tries'} left`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
      <Link to="/marketplace" className="btn-ghost inline-flex items-center gap-1 text-xs px-2 py-1 rounded" data-testid="agent-detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to marketplace
      </Link>

      <div className="mt-6 card-base p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -right-12 -top-16 h-60 w-60 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-16 -bottom-20 h-56 w-56 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row gap-6 items-start">
          <AgentAvatar name={agent.name} size={84} />
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">{agent.category}</span>
                <h1 className="display text-3xl md:text-4xl font-semibold mt-1">{agent.name}</h1>
                <div className="text-white/65 mt-1">{agent.role}</div>
              </div>
              <div className="flex items-center gap-2">
                {agent.featured && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#FF6B1A]/35 text-[#FFB17A] bg-[#FF6B1A]/10 inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Featured
                  </span>
                )}
                <span className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full hairline text-white/70">{agent.model_provider}/{agent.model_name}</span>
              </div>
            </div>
            <p className="mt-4 text-white/75 max-w-2xl">{agent.description}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid={AGENT_DETAIL.tryNow}
                onClick={() => navigate(`/chat?agent=${agent.agent_id}`)}
                className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium"
              >
                <MessageSquare className="h-4 w-4" /> {tryLabel}
              </button>
              {agent.starter_prompt && (
                <button
                  onClick={onTryStarter}
                  data-testid="agent-detail-starter-prompt-button"
                  className="btn-secondary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm"
                >
                  <Wand2 className="h-4 w-4 text-[#39FF14]" /> Try the demo prompt
                </button>
              )}
              <button
                onClick={() => navigate(`/builder?fork=${agent.agent_id}`)}
                data-testid="agent-detail-fork-button"
                className="btn-secondary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm"
              >
                <GitBranch className="h-4 w-4 text-[#39FF14]" /> Fork into Builder
              </button>
              <button
                onClick={onShare}
                data-testid="agent-detail-share-button"
                className="btn-ghost inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm"
              >
                <Share2 className="h-4 w-4 text-[#FF6B1A]" /> Copy share link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Starter prompt + output preview */}
      {(agent.starter_prompt || agent.output_example) && (
        <div className="mt-6 grid lg:grid-cols-2 gap-4">
          {agent.starter_prompt && (
            <div className="card-base p-6 relative overflow-hidden" data-testid="agent-detail-starter-prompt-card">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#39FF14]/10 blur-2xl pointer-events-none" />
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="h-4 w-4 text-[#39FF14]" />
                <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// try this exact prompt</span>
              </div>
              <p className="text-sm text-white/85 leading-relaxed mono bg-[hsl(225,18%,5%)] hairline rounded-lg p-3 whitespace-pre-wrap">
                {agent.starter_prompt}
              </p>
              <button
                onClick={onTryStarter}
                data-testid="agent-detail-run-starter-prompt"
                className="btn-primary mt-4 w-full rounded-xl px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"
              >
                Run this prompt <Zap className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {agent.output_example && (
            <div className="card-base p-6 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#FF6B1A]/10 blur-2xl pointer-events-none" />
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-4 w-4 text-[#FF6B1A]" />
                <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// what you'll get</span>
              </div>
              <p className="display text-lg font-semibold leading-snug">{agent.output_example}</p>
              <p className="text-xs text-white/55 mt-3">
                Every response is structured, paste-ready, and tuned for an operator's workflow. No fluff. No wall-of-text markdown noise.
              </p>
            </div>
          )}
        </div>
      )}

      <div data-testid={AGENT_DETAIL.tabs} className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="card-base p-6 lg:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-[#FF6B1A] mb-2">// capabilities</div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {(agent.capabilities && agent.capabilities.length > 0 ? agent.capabilities : ['Tuned prompt', 'Multi-step reasoning', 'Streaming-ready']).map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm text-white/80">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#39FF14]" />
                {c}
              </li>
            ))}
          </ul>
          <div className="text-[10px] uppercase tracking-widest text-[#FF6B1A] mb-2 mt-6">// best use cases</div>
          <div className="flex flex-wrap gap-2">
            {(agent.use_cases && agent.use_cases.length > 0 ? agent.use_cases : ['Founder-led GTM', 'Async ops', 'Pipeline acceleration']).map((u) => (
              <span key={u} className="text-xs px-3 py-1.5 rounded-full hairline text-white/75">{u}</span>
            ))}
          </div>
        </div>
        <div className="card-base p-6">
          <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// pricing</div>
          <div className="display text-3xl font-semibold">{agent.pricing_label || 'Free try • $49/mo Pro'}</div>
          <p className="text-sm text-white/60 mt-2">2 free tries per agent — no card required. Sign up to unlock unlimited chat with all 18 agents.</p>
          <Link to="/pricing" className="btn-secondary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs mt-4">
            <Zap className="h-3.5 w-3.5" /> See plans
          </Link>
        </div>
      </div>
    </div>
  );
}
